import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";
import ApiRequest from "../../helper/ApiRequest";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import AdditionalSchemas, { normalizeAdditionalSchemas } from "../Seo/AdditionalSchemas";

const schemaViewEndpoint = ApiEndpoints.globalSeo?.view || ApiEndpoints.schemaManagement.view;
const schemaUpdateEndpoint = ApiEndpoints.globalSeo?.update || ApiEndpoints.schemaManagement.update;

const validateSchemaJson = (schema, label) => {
  const value = String(schema || "").trim();
  const errors = [];

  if (!value) return errors;

  if (/<\/?script\b/i.test(value)) {
    errors.push(`${label} must not include script tags. Add only the JSON schema value.`);
    return errors;
  }

  try {
    JSON.parse(value);
  } catch (error) {
    errors.push(`${label} must be valid JSON.`);
  }

  return errors;
};

const validateAdditionalSchemas = (schemas = []) => {
  const errors = [];

  schemas.forEach((schema, index) => {
    const label = schema.schema_label?.trim() || `Additional schema ${index + 1}`;
    const value = schema.schema_value?.trim();

    if (!value) return;

    if (/<\/?script\b/i.test(value)) {
      errors.push(`${label} must not include script tags. Add only the JSON schema value.`);
      return;
    }

    try {
      JSON.parse(value);
    } catch (error) {
      errors.push(`${label} must be valid JSON.`);
    }
  });

  return errors;
};

export default function SchemaManagement() {
  const [globalSeo, setGlobalSeo] = useState({
    review_schema: "",
    organisation_schema: "",
    twitter_site: "",
    twitter_card: "summary_large_image",
    twitter_creator: "",
    og_site_name: "",
    additional_schemas: [],
  });
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const pagename = {
    name: "Global SEO",
    user: "Admin",
    innerpage: "Global SEO",
  };

  const fetchSchema = async () => {
    try {
      const response = await ApiRequest("GET", schemaViewEndpoint);
      setGlobalSeo((current) => ({
        ...current,
        ...(response?.schema || {}),
        twitter_card: response?.schema?.twitter_card || current.twitter_card,
        additional_schemas: normalizeAdditionalSchemas(response?.schema?.additional_schemas),
      }));
    } catch (error) {
      console.error("Error loading schema:", error);
      toast.error("Unable to load schema.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchema();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setGlobalSeo((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    const validationErrors = [
      ...validateSchemaJson(globalSeo.review_schema, "Review schema"),
      ...validateSchemaJson(globalSeo.organisation_schema, "Organization schema"),
      ...validateAdditionalSchemas(globalSeo.additional_schemas),
    ];

    if (validationErrors.length) {
      setErrors(validationErrors);
      toast.error(validationErrors[0]);
      return;
    }

    try {
      setSaving(true);
      const response = await ApiRequest("POST", schemaUpdateEndpoint, globalSeo);
      toast.success(response.message);
    } catch (error) {
      const apiErrors = error?.response?.data?.errors || [];
      setErrors(apiErrors);
      toast.error(error?.response?.data?.message || "Unable to update schema.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AdminHeader pagename={pagename} />

      <div className="add-content-page">
        <form onSubmit={handleSubmit}>
          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Twitter Site</label>
                  </div>
                  <input
                    type="text"
                    name="twitter_site"
                    value={globalSeo.twitter_site}
                    onChange={handleChange}
                    className="form-control"
                    disabled={loading || saving}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Twitter Card</label>
                  </div>
                  <input
                    type="text"
                    name="twitter_card"
                    value={globalSeo.twitter_card}
                    onChange={handleChange}
                    className="form-control"
                    disabled={loading || saving}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Twitter Creator</label>
                  </div>
                  <input
                    type="text"
                    name="twitter_creator"
                    value={globalSeo.twitter_creator}
                    onChange={handleChange}
                    className="form-control"
                    disabled={loading || saving}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>OG Site Name</label>
                  </div>
                  <input
                    type="text"
                    name="og_site_name"
                    value={globalSeo.og_site_name}
                    onChange={handleChange}
                    className="form-control"
                    disabled={loading || saving}
                  />
                </div>
              </div>

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Review Schema</label>
                  </div>
                  <textarea
                    name="review_schema"
                    value={globalSeo.review_schema}
                    onChange={handleChange}
                    className="form-control"
                    rows="14"
                    disabled={loading || saving}
                    style={{ height: "auto", fontFamily: "monospace", whiteSpace: "pre", overflow: "auto" }}
                  />
                </div>
              </div>

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Organization Schema</label>
                  </div>
                  <textarea
                    name="organisation_schema"
                    value={globalSeo.organisation_schema}
                    onChange={handleChange}
                    className="form-control"
                    rows="14"
                    disabled={loading || saving}
                    style={{ height: "auto", fontFamily: "monospace", whiteSpace: "pre", overflow: "auto" }}
                  />
                </div>
              </div>
            </div>
          </div>

          <AdditionalSchemas
            value={globalSeo.additional_schemas}
            onChange={(additional_schemas) =>
              setGlobalSeo((current) => ({ ...current, additional_schemas }))
            }
            disabled={loading || saving}
          />

          <div className="add-content-wrap">
            <div className="row">
              {errors.length > 0 && (
                <div className="col-lg-12">
                  <div className="alert alert-danger">
                    {errors.map((error) => (
                      <div key={error}>{error}</div>
                    ))}
                  </div>
                </div>
              )}

              <div className="col-lg-12">
                <button className="default-btn" type="submit" disabled={loading || saving}>
                  {saving ? "Saving..." : "Save Global SEO"}<span></span>
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
